import { NamedProperty } from "sprint-common-web";

import { BuzzContentReferenceResponse } from "../../../common/api/catalogue-api/buzz/BuzzContentReferenceResponse";
import {
    MovieDocumentResponse,
    MovieVersionDocumentResponse,
    ShreqContentReferenceResponse
} from "../../../common/api/catalogue-api/movie/MovieDocumentResponse";
import { MovieResponse, MovieVersionResponse } from "../../../common/api/catalogue-api/movie/MovieResponse";
import { VersionType } from "../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { Airline } from "../../../common/types/Airline";
import { ContentSource } from "../../../common/types/ContentSource";

export interface MovieItem {
    id: string;
    title: string;
    year?: number;
    orphaned: boolean;
    includedSources: ContentSource[];
    versions: MovieItemVersion[];
    isModified?: boolean;
    merged?: boolean;
    orphanedDetails: string[];
    buzz?: BuzzContentReferenceResponse;
    shreq?: ShreqContentReferenceResponse;
}

export interface MovieItemVersion {
    id: string;
    type: VersionType;
    brandings: Airline[];
    distributor: NamedProperty;
    merged: boolean;
}

const mapVersion = (version: MovieVersionDocumentResponse | MovieVersionResponse): MovieItemVersion => ({
    id: version.id,
    type: version.type,
    brandings: version.brandings ?? [],
    distributor: version.distributor,
    merged: !!version.mergedTargetId
});

export const mapDocumentToMovieItem = (item: MovieDocumentResponse): MovieItem => ({
    id: item.id,
    title: item.title,
    year: item.year,
    buzz: item.buzz,
    shreq: item.shreq,
    orphaned: item.orphaned,
    includedSources: item.includedSources,
    versions: item.versions.map(mapVersion),
    merged: !!item.mergedTargetId,
    orphanedDetails: mapToOrphanedDetails(item)
});

const mapToOrphanedDetails = (item: MovieDocumentResponse): string[] => {
    if (item.orphaned && item.orphanedVersionsCount > 0) {
        return [`Orphaned versions count: ${item.orphanedVersionsCount}`];
    }
    return [];
};

export const mapMergeResponseToMovieItem = (item: MovieResponse): MovieItem => ({
    id: item.id,
    title: item.title,
    year: item.year,
    buzz: item.buzz,
    shreq: item.shreq,
    orphaned: false,
    includedSources: [deriveSource(item)],
    versions: item.versions.map(mapVersion),
    merged: !!item.mergedTargetId,
    orphanedDetails: []
});

const deriveSource = (item: MovieResponse): ContentSource => {
    if (item.shreq) return ContentSource.Shreq;
    if (item.buzz) return ContentSource.Buzz;
    return ContentSource.Sprint;
};

export const getOrphanedDetails = (movie?: MovieDocumentResponse): string[] => {
    if (!movie?.orphaned) return [];

    if (movie.orphanedVersionsCount > 0) {
        return [`Orphaned versions count: ${movie.orphanedVersionsCount}`];
    }

    return [];
};
